import * as React from 'react';
import { Link } from 'react-router-dom';
import usePageConfig from 'hooks/use-page-config';
import useAppLanguage from 'hooks/use-app-language';
import OtpInput from '../../components/OtpInput';
import { TwoFactorLoginResponse } from '../../entities/login';
import { useTwoFactorLogin } from '../../hooks/use-user-login';
import messages from './messages';
import { LoginTwoFactorState } from './models/LoginTwoFactorState';

export default function LoginTwoFactor({
  twoFactorToken,
  onTwoFactorValidated,
}: {
  twoFactorToken: string;
  onTwoFactorValidated: () => void;
}) {
  // #region HOOKS
  const language = useAppLanguage();
  const [twoFactorLogin] = useTwoFactorLogin();
  const pageConfig = usePageConfig(); // TODO: usePageConfig for support email
  // #endregion

  // #region STATE
  const [state, setState] = React.useState<LoginTwoFactorState>({});
  // #endregion

  // #region TRANSLATIONS
  const loginTwoFactorMessages = messages[language].loginTwoFactor;
  // #endregion

  // #region FUNCTIONS
  function onValidateTwoFactorCallback(response: TwoFactorLoginResponse) {
    if (response.success) onTwoFactorValidated();
    else {
      setState({
        ...state,
        isTwoFactorCodeValid: false,
        message: response?.displayMessage?.includes('2FA verification failed')
          ? loginTwoFactorMessages.twoFactorErrorMessage
          : response.displayMessage || response.message,
        showLockMessage: response?.displayMessage?.includes(
          'your account has been locked',
        ),
      });
    }
  }

  function onValidateTwoFactor(value: string) {
    setState({ ...state, isTwoFactorCodeValid: true });

    if (value.length === 6)
      twoFactorLogin({
        pin: value,
        token: twoFactorToken,
        callback: onValidateTwoFactorCallback,
      });
  }
  // #endregion

  return (
    <div className="flex flex-col bg-ghost-white">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <h2 className="font-semibold leading-9 mt-6 text-2.5x1 text-black text-center tracking-wide">
          {loginTwoFactorMessages.heading}
        </h2>
        <p className="font-normal mt-3 text-center text-gray-900 text-lg tracking-wide mx-6 sm:mx-0">
          {loginTwoFactorMessages.subHeading}
        </p>
      </div>
      <div className="mt-16 mb-16 sm:mx-auto sm:w-full text-center">
        <OtpInput
          isNumberInput
          length={6}
          onChangeOtp={onValidateTwoFactor}
          warning={state.isTwoFactorCodeValid === false}
          warningMessage={state.message}
        />
      </div>

      <div className="sm:mx-auto sm:w-full text-1.06x1">
        <div className="m-6">
          <div className="sm:mx-auto sm:w-full sm:max-w-md text-center">
            <p className="leading-9 m-6 text-black text-center">
              {loginTwoFactorMessages.havingTroubleLabel}
            </p>
            <p className="text-gray-900">
              {loginTwoFactorMessages.supportLabelBeforeEmail}&nbsp;
              <a
                href={`mailto:${pageConfig?.App?.SupportEmail}`}
                className="underline"
              >
                {pageConfig?.App?.SupportEmail}
              </a>
              &nbsp;
              {pageConfig?.App?.SupportEmail}
            </p>
          </div>
          <div className="mt-6">
            <span className="block text-center">
              <Link
                to="/reset-two-factor"
                className="font-semibold text-right text-vivid-blue underline hover:no-underline"
              >
                {loginTwoFactorMessages.resetTwoFactorLink}
              </Link>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
